import { useRef, useState } from "react"
import { Answers, AnswerValue, Question } from "@/types/agent"

import { sendAnalyticsEvent } from "@/lib/analytics/amplitude-utils"
import { useTranslation } from "@/lib/i18n/i18n-context"
import {
  getDefaultValue,
  isAnswerValid,
  QuestionInput,
} from "./agent-question-inputs"
import { AutoResizingTextarea } from "./auto-resizing-textarea"
import { Button } from "./ui/button"

interface AgentQuestionFormProps {
  questions: Question[]
  onSubmit: (answers: Answers, skippedQuestionIds?: string[]) => void
  disabled?: boolean
  /** One single-choice question: hide Finish/Something else and submit on pick. */
  singleSelectInstantSubmit?: boolean
  /** One OTP question: hide Finish and submit once all digits are filled. */
  otpInstantSubmit?: boolean
  analyticsEnabled?: boolean
  messageId?: number
}

// Initialize answers with default values from questions
function initializeAnswers(questions: Question[]): Answers {
  const initial: Answers = {}
  for (const q of questions) {
    const defaultVal = getDefaultValue(q)
    if (defaultVal !== undefined) {
      initial[q.id] = defaultVal
    }
  }
  return initial
}

export function AgentQuestionForm({
  questions,
  onSubmit,
  disabled = false,
  singleSelectInstantSubmit = false,
  otpInstantSubmit = false,
  analyticsEnabled = false,
  messageId,
}: AgentQuestionFormProps) {
  const t = useTranslation()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [answers, setAnswers] = useState<Answers>(() =>
    initializeAnswers(questions)
  )
  const [customAnswers, setCustomAnswers] = useState<Record<string, string>>({})
  const [skippedQuestionIds, setSkippedQuestionIds] = useState<string[]>([])
  const otherAnalyticsSentRef = useRef<Set<string>>(new Set())

  const isMultiQuestionForm = questions.length > 1
  const currentQuestion = questions[currentIndex]
  const currentAnswer = answers[currentQuestion.id]
  const customAnswer = customAnswers[currentQuestion.id] || ""
  const isMultiValueSelect =
    currentQuestion.input_type === "multi_select" ||
    currentQuestion.input_type === "pills"
  const isRangeInput =
    currentQuestion.input_type === "range" ||
    currentQuestion.input_type === "dual_range"

  const hideActionButtons = singleSelectInstantSubmit || otpInstantSubmit

  const trackSomethingElseEngagement = () => {
    if (!analyticsEnabled || messageId === undefined) return
    if (otherAnalyticsSentRef.current.has(currentQuestion.id)) return
    otherAnalyticsSentRef.current.add(currentQuestion.id)
    sendAnalyticsEvent({
      name: "ClickClarifyingQuestionOther",
      properties: {
        messageId,
        questionIndex: currentIndex,
        inputType: currentQuestion.input_type,
      },
    })
  }

  const handleAnswerChange = (value: AnswerValue) => {
    const nextSkippedQuestionIds = skippedQuestionIds.filter(
      (id) => id !== currentQuestion.id
    )
    const nextAnswers = { ...answers, [currentQuestion.id]: value }
    setSkippedQuestionIds(nextSkippedQuestionIds)
    setAnswers(nextAnswers)

    if (singleSelectInstantSubmit) {
      onSubmit(nextAnswers, nextSkippedQuestionIds)
      return
    }

    if (otpInstantSubmit && isAnswerValid(currentQuestion, value)) {
      onSubmit(nextAnswers, nextSkippedQuestionIds)
      return
    }

    if (!isMultiValueSelect) {
      setCustomAnswers({ ...customAnswers, [currentQuestion.id]: "" })
    }
  }

  const clearPredefinedSelectionForCustomText = () => {
    if (isMultiValueSelect) return

    if (
      currentQuestion.input_type === "single_select" ||
      currentQuestion.input_type === "dropdown"
    ) {
      setAnswers({ ...answers, [currentQuestion.id]: "" })
    } else if (currentQuestion.input_type === "toggle" || isRangeInput) {
      const { [currentQuestion.id]: _, ...nextAnswers } = answers
      setAnswers(nextAnswers)
    }
  }

  const handleCustomAnswerChange = (value: string) => {
    if (value.trim().length > 0) {
      trackSomethingElseEngagement()
      clearPredefinedSelectionForCustomText()
    }
    setCustomAnswers({ ...customAnswers, [currentQuestion.id]: value })
  }

  const handleCustomInputFocus = () => {
    trackSomethingElseEngagement()
  }

  const handleContinue = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1)
    } else {
      handleSubmit()
    }
  }

  const handleBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1)
    }
  }

  const handleSkipQuestion = () => {
    const nextSkippedQuestionIds = skippedQuestionIds.includes(
      currentQuestion.id
    )
      ? skippedQuestionIds
      : [...skippedQuestionIds, currentQuestion.id]

    const { [currentQuestion.id]: _, ...nextAnswers } = answers
    const { [currentQuestion.id]: __, ...nextCustomAnswers } = customAnswers
    setSkippedQuestionIds(nextSkippedQuestionIds)
    setAnswers(nextAnswers)
    setCustomAnswers(nextCustomAnswers)

    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1)
    } else {
      handleSubmit(nextAnswers, nextCustomAnswers, nextSkippedQuestionIds)
    }
  }

  const handleSubmit = (
    answersToSubmit = answers,
    customAnswersToSubmit = customAnswers,
    skippedQuestionIdsToSubmit = skippedQuestionIds
  ) => {
    const finalAnswers: Answers = { ...answersToSubmit }
    Object.keys(customAnswersToSubmit).forEach((qId) => {
      if (skippedQuestionIdsToSubmit.includes(qId)) return

      const custom = customAnswersToSubmit[qId].trim()
      if (!custom) return

      const question = questions.find((q) => q.id === qId)
      if (
        question?.input_type === "multi_select" ||
        question?.input_type === "pills"
      ) {
        const existing = finalAnswers[qId]
        const existingArray = Array.isArray(existing) ? existing : []
        finalAnswers[qId] = [...existingArray, custom]
      } else {
        finalAnswers[qId] = custom
      }
    })
    onSubmit(finalAnswers, skippedQuestionIdsToSubmit)
  }

  const isQuestionAnswered = (question: Question) => {
    const value = answers[question.id]
    const custom = customAnswers[question.id] || ""
    return isAnswerValid(question, value) || custom.trim().length > 0
  }

  const isCurrentAnswered = isQuestionAnswered(currentQuestion)
  const isLastQuestion = currentIndex === questions.length - 1
  const isCurrentMandatory = currentQuestion.mandatory === true

  const isPillInput =
    currentQuestion.input_type === "single_select" ||
    currentQuestion.input_type === "multi_select" ||
    currentQuestion.input_type === "pills" ||
    currentQuestion.input_type === "toggle"

  const isDropdownInput = currentQuestion.input_type === "dropdown"

  const showSomethingElseField =
    isMultiQuestionForm &&
    currentQuestion.input_type !== "text" &&
    currentQuestion.input_type !== "number" &&
    currentQuestion.input_type !== "otp" &&
    currentQuestion.input_type !== "multi_field" &&
    currentQuestion.input_type !== "form" &&
    (isPillInput || isDropdownInput || isRangeInput)

  const canContinue = isCurrentAnswered

  const isFormQuestion = currentQuestion.input_type === "form"

  let continueLabel = t("next")
  if (isLastQuestion) {
    continueLabel =
      questions.length === 1 || isFormQuestion ? t("submit") : t("finish")
  }

  return (
    <div className="mt-3">
      {isMultiQuestionForm && (
        <h2 className="font-semibold">
          {t("questionXOfY", {
            current: currentIndex + 1,
            total: questions.length,
          })}
        </h2>
      )}

      {isFormQuestion && currentQuestion.title && (
        <div className="mb-5">
          <h2 className="font-semibold">{currentQuestion.title}</h2>
          {currentQuestion.config?.description && (
            <p className="mt-1 text-xs text-pretty text-muted">
              {currentQuestion.config.description}
            </p>
          )}
        </div>
      )}

      <div className="prose prose-chat">
        {/* A titled form's header (title + description) replaces the question_text lead;
            question_text still carries the prose for non-form clients and the answered summary. */}
        {currentQuestion.question_text &&
          !(isFormQuestion && currentQuestion.title) && (
            <p className="text-pretty">{currentQuestion.question_text}</p>
          )}

        <QuestionInput
          key={currentQuestion.id}
          question={currentQuestion}
          value={currentAnswer}
          onChange={handleAnswerChange}
          disabled={disabled}
          suppressPillDefault={customAnswer.trim().length > 0}
        />

        {showSomethingElseField && (
          <div className="mt-4">
            <AutoResizingTextarea
              value={customAnswer}
              onChange={(e) => handleCustomAnswerChange(e.target.value)}
              onFocus={handleCustomInputFocus}
              placeholder={t("somethingElsePlaceholder")}
              disabled={disabled}
              rows={1}
              className="w-full rounded-lg border border-input px-3 py-1.5 text-sm leading-4 focus:border-current"
            />
          </div>
        )}
      </div>

      {!hideActionButtons && (
        <div className="mt-5 flex gap-2">
          {currentIndex > 0 && (
            <Button
              size="md"
              variant="outline"
              onClick={handleBack}
              disabled={disabled}
            >
              {t("back")}
            </Button>
          )}
          <Button
            size="md"
            variant="primary"
            onClick={handleContinue}
            disabled={!canContinue || disabled}
          >
            {continueLabel}
          </Button>
          {!isCurrentAnswered && !isCurrentMandatory && (
            <Button
              variant="ghost"
              size="md"
              className="text-muted italic hover:text-foreground"
              onClick={handleSkipQuestion}
              disabled={disabled}
            >
              {t("skip")}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
